import { useState, useEffect, useRef } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function PayrollReports() {
  const [selectedPeriod, setSelectedPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [reportType, setReportType] = useState<'monthly' | 'yearly' | 'evolution'>('monthly');
  const [payrolls, setPayrolls] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const reportRef = useRef<HTMLDivElement>(null); // Référence au contenu principal du rapport

  // Formatage monétaire en GNF
  const formatGNF = (value: number): string => {
    if (value === 0) return '0,00 GNF';
    return value.toLocaleString('fr-GN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' GNF';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [payrollsRes, employeesRes] = await Promise.all([
          getResources('bulletins-paie'),
          getResources('employes'),
        ]);

        const payrollsData = payrollsRes.data;
        const employeesData = employeesRes.data;

        const toNumber = (v: any) => Number(v) || 0;

        const mappedPayrolls = payrollsData.map((p: any) => ({
          id: p.id,
          employeeId: p.employe_id,

          employee: p.employe ? {
            id: p.employe.id,
            firstName: p.employe.prenom,
            lastName: p.employe.nom,
            position: p.employe.poste,
            department: p.employe.departement || 'Non défini',
          } : null,

          period: `${p.annee}-${String(p.mois).padStart(2, '0')}`,
          grossSalary: toNumber(p.salaire_brut),
          netSalary: toNumber(p.net_a_payer),
          employeeCharges: toNumber(p.total_deductions),
          employerCharges: toNumber(p.total_charges_patronales),
        }));

        const mappedEmployees = employeesData.map((e: any) => ({
          id: e.id,
          firstName: e.prenom,
          lastName: e.nom,
          position: e.poste,
          department: e.departement || 'Non défini',
          baseSalary: toNumber(e.salaire_base),
        }));

        setPayrolls(mappedPayrolls);
        setEmployees(mappedEmployees);

        // Extraction des années et périodes disponibles
        const years = [...new Set(mappedPayrolls.map(p => p.period.split('-')[0]))]
          .sort((a, b) => Number(b) - Number(a));
        const periods = [...new Set(mappedPayrolls.map(p => p.period))]
          .sort((a, b) => b.localeCompare(a));

        setAvailableYears(years.length > 0 ? years : [String(new Date().getFullYear())]);
        setAvailablePeriods(periods.length > 0 ? periods : [new Date().toISOString().slice(0, 7)]);

        if (years.length > 0) setSelectedYear(years[0]);
        if (periods.length > 0) setSelectedPeriod(periods[0]);

      } catch (error) {
        console.error('Erreur lors du chargement des données :', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculs selon le type de rapport
  const monthlyPayrolls = payrolls.filter(p => p.period === selectedPeriod);
  const monthlyStats = {
    totalEmployees: monthlyPayrolls.length,
    totalGrossSalary: monthlyPayrolls.reduce((s, p) => s + p.grossSalary, 0),
    totalNetSalary: monthlyPayrolls.reduce((s, p) => s + p.netSalary, 0),
    totalEmployeeCharges: monthlyPayrolls.reduce((s, p) => s + p.employeeCharges, 0),
    totalEmployerCharges: monthlyPayrolls.reduce((s, p) => s + p.employerCharges, 0),
    totalEmployerCost: monthlyPayrolls.reduce((s, p) => s + p.grossSalary + p.employerCharges, 0),
  };

  const yearlyPayrolls = payrolls.filter(p => p.period.startsWith(selectedYear));
  const yearlyStats = {
    totalPayrolls: yearlyPayrolls.length,
    totalGrossSalary: yearlyPayrolls.reduce((s, p) => s + p.grossSalary, 0),
    totalNetSalary: yearlyPayrolls.reduce((s, p) => s + p.netSalary, 0),
    totalEmployeeCharges: yearlyPayrolls.reduce((s, p) => s + p.employeeCharges, 0),
    totalEmployerCharges: yearlyPayrolls.reduce((s, p) => s + p.employerCharges, 0),
    totalEmployerCost: yearlyPayrolls.reduce((s, p) => s + p.grossSalary + p.employerCharges, 0),
  };

  const departmentStats = employees.reduce((acc: any, emp) => {
    const dept = emp.department;
    if (!acc[dept]) acc[dept] = { employees: 0, totalSalary: 0 };
    acc[dept].employees++;
    acc[dept].totalSalary += emp.baseSalary;
    return acc;
  }, {});

  const monthlyEvolution = Array.from({ length: 12 }, (_, i) => {
    const monthNum = (i + 1).toString().padStart(2, '0');
    const period = `${selectedYear}-${monthNum}`;
    const monthPayrolls = payrolls.filter(p => p.period === period);
    return {
      month: new Date(`${selectedYear}-${monthNum}-01`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
      employees: monthPayrolls.length,
      grossSalary: monthPayrolls.reduce((s, p) => s + p.grossSalary, 0),
      netSalary: monthPayrolls.reduce((s, p) => s + p.netSalary, 0),
      charges: monthPayrolls.reduce((s, p) => s + p.employeeCharges + p.employerCharges, 0),
    };
  });

  // Export Excel dynamique
  const exportToExcel = () => {
    let data: any[] = [];
    let filename = '';
    let sheetName = '';

    if (reportType === 'monthly') {
      filename = `rapport_mensuel_${selectedPeriod}.xlsx`;
      sheetName = 'Rapport Mensuel';
      data = monthlyPayrolls.map(p => {
        const emp = employees.find(e => e.id === p.employeeId);
        return {
          Employé: `${emp?.firstName || ''} ${emp?.lastName || ''}`.trim() || 'Inconnu',
          Poste: emp?.position || '',
          'Salaire Brut': p.grossSalary,
          'Charges Salariales': p.employeeCharges,
          'Salaire Net': p.netSalary,
          'Charges Patronales': p.employerCharges,
          'Coût Total': p.grossSalary + p.employerCharges,
        };
      });
      data.push({
        Employé: 'TOTAUX',
        'Salaire Brut': monthlyStats.totalGrossSalary,
        'Charges Salariales': monthlyStats.totalEmployeeCharges,
        'Salaire Net': monthlyStats.totalNetSalary,
        'Charges Patronales': monthlyStats.totalEmployerCharges,
        'Coût Total': monthlyStats.totalEmployerCost,
      });

    } else if (reportType === 'yearly') {
      filename = `rapport_annuel_${selectedYear}.xlsx`;
      sheetName = 'Répartition Départements';
      data = Object.entries(departmentStats).map(([dept, stats]: any) => ({
        Département: dept,
        Employés: stats.employees,
        'Masse Salariale Mensuelle': stats.totalSalary,
        'Salaire Moyen': Math.round(stats.totalSalary / stats.employees),
      }));

    } else if (reportType === 'evolution') {
      filename = `evolution_${selectedYear}.xlsx`;
      sheetName = 'Évolution Mensuelle';
      data = monthlyEvolution.map(m => ({
        Mois: m.month,
        Employés: m.employees,
        'Salaire Brut': m.grossSalary,
        'Salaire Net': m.netSalary,
        'Charges Sociales': m.charges,
      }));
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, filename);
  };

  // Export PDF : capture du contenu visible
  const exportToPDF = async () => {
    if (!reportRef.current) return;

    const canvas = await html2canvas(reportRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);

    const filename = reportType === 'monthly'
      ? `rapport_mensuel_${selectedPeriod}.pdf`
      : reportType === 'yearly'
      ? `rapport_annuel_${selectedYear}.pdf`
      : `evolution_${selectedYear}.pdf`;

    pdf.save(filename);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Chargement des rapports...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sélecteurs */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Rapports de Paie</h2>
            <p className="text-sm text-gray-600">Analyse des coûts du personnel et des charges sociales</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="monthly">Rapport Mensuel</option>
              <option value="yearly">Rapport Annuel</option>
              <option value="evolution">Évolution Annuelle</option>
            </select>

            {reportType === 'monthly' && (
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {availablePeriods.map(p => (
                  <option key={p} value={p}>
                    {new Date(p + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </option>
                ))}
              </select>
            )}

            {(reportType === 'yearly' || reportType === 'evolution') && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Contenu principal du rapport – capturé pour le PDF */}
      <div ref={reportRef} className="space-y-6">
        {/* Rapport Mensuel */}
        {reportType === 'monthly' && (
          <>
            {/* Cartes statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border flex items-center">
                <div className="p-3 rounded-full bg-blue-100 mr-4">
                  <i className="ri-team-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Employés Payés</p>
                  <p className="text-2xl font-bold text-gray-900">{monthlyStats.totalEmployees}</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border flex items-center">
                <div className="p-3 rounded-full bg-green-100 mr-4">
                  <i className="ri-money-dollar-circle-line text-xl text-green-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Salaires Nets Payés</p>
                  <p className="text-2xl font-bold text-gray-900">{formatGNF(monthlyStats.totalNetSalary)}</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border flex items-center">
                <div className="p-3 rounded-full bg-purple-100 mr-4">
                  <i className="ri-calculator-line text-xl text-purple-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Coût Total Employeur</p>
                  <p className="text-2xl font-bold text-gray-900">{formatGNF(monthlyStats.totalEmployerCost)}</p>
                </div>
              </div>
            </div>

            {/* Détail des coûts */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Détail des Coûts - {new Date(selectedPeriod + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </h3>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Charges Salariales</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Salaire Brut Total:</span><span>{formatGNF(monthlyStats.totalGrossSalary)}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Charges Salariales:</span><span className="text-red-600">-{formatGNF(monthlyStats.totalEmployeeCharges)}</span></div>
                    <div className="border-t pt-3 flex justify-between font-semibold text-green-700">
                      <span>Salaire Net Total:</span><span>{formatGNF(monthlyStats.totalNetSalary)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Charges Patronales</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Salaire Brut Total:</span><span>{formatGNF(monthlyStats.totalGrossSalary)}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Charges Patronales:</span><span className="text-blue-600">+{formatGNF(monthlyStats.totalEmployerCharges)}</span></div>
                    <div className="border-t pt-3 flex justify-between font-semibold text-blue-700">
                      <span>Coût Total Employeur:</span><span>{formatGNF(monthlyStats.totalEmployerCost)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tableau des bulletins */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Bulletins de Paie du Mois</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salaire Brut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Charges Salariales</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salaire Net</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Charges Patronales</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coût Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {monthlyPayrolls.map(p => {
                      const emp = p.employee;

                      return (
                        <tr key={p.id}>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {emp ? `${emp.firstName} ${emp.lastName}` : 'Employé inconnu'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {emp?.position || '-'}
                            </div>
                          </td>
                          <td>{formatGNF(p.grossSalary)}</td>
                          <td className="text-red-600">-{formatGNF(p.employeeCharges)}</td>
                          <td className="text-green-600">{formatGNF(p.netSalary)}</td>
                          <td className="text-blue-600">{formatGNF(p.employerCharges)}</td>
                          <td>{formatGNF(p.grossSalary + p.employerCharges)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {reportType === 'monthly' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 mr-4">
                      <i className="ri-team-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Employés Payés</p>
                      <p className="text-2xl font-bold text-gray-900">{monthlyStats.totalEmployees}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100 mr-4">
                      <i className="ri-money-dollar-circle-line text-xl text-green-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Salaires Nets Payés</p>
                      <p className="text-2xl font-bold text-gray-900">{formatGNF(monthlyStats.totalNetSalary)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100 mr-4">
                      <i className="ri-calculator-line text-xl text-purple-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Coût Total Employeur</p>
                      <p className="text-2xl font-bold text-gray-900">{formatGNF(monthlyStats.totalEmployerCost)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Détail des Coûts - {new Date(selectedPeriod + '-01').toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Charges Salariales</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between"><span className="text-sm text-gray-600">Salaire Brut Total:</span><span className="text-sm font-medium">{formatGNF(monthlyStats.totalGrossSalary)}</span></div>
                        <div className="flex justify-between"><span className="text-sm text-gray-600">Charges Salariales:</span><span className="text-sm font-medium text-red-600">-{formatGNF(monthlyStats.totalEmployeeCharges)}</span></div>
                        <div className="border-t pt-2 flex justify-between font-medium">
                          <span className="text-sm text-green-700">Salaire Net Total:</span>
                          <span className="text-sm text-green-700">{formatGNF(monthlyStats.totalNetSalary)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Charges Patronales</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between"><span className="text-sm text-gray-600">Salaire Brut Total:</span><span className="text-sm font-medium">{formatGNF(monthlyStats.totalGrossSalary)}</span></div>
                        <div className="flex justify-between"><span className="text-sm text-gray-600">Charges Patronales:</span><span className="text-sm font-medium text-blue-600">+{formatGNF(monthlyStats.totalEmployerCharges)}</span></div>
                        <div className="border-t pt-2 flex justify-between font-medium">
                          <span className="text-sm text-blue-700">Coût Total Employeur:</span>
                          <span className="text-sm text-blue-700">{formatGNF(monthlyStats.totalEmployerCost)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

             <div className="bg-white rounded-lg shadow-sm border">
  <div className="px-6 py-4 border-b border-gray-200">
    <h3 className="text-lg font-medium text-gray-900">
      Bulletins de Paie du Mois
    </h3>
  </div>

  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salaire Brut</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Charges Salariales</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salaire Net</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Charges Patronales</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coût Total</th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-gray-200">
        {monthlyPayrolls.map((payroll) => {
          const emp = payroll.employee; // ✅ SOURCE UNIQUE

          return (
            <tr key={payroll.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {emp ? `${emp.firstName} ${emp.lastName}` : 'Employé inconnu'}
                </div>
                <div className="text-sm text-gray-500">
                  {emp?.position || '-'}
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatGNF(payroll.grossSalary)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                -{formatGNF(payroll.employeeCharges)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                {formatGNF(payroll.netSalary)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                {formatGNF(payroll.employerCharges)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {formatGNF(payroll.grossSalary + payroll.employerCharges)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
</div>

            </>
          )}

          {/* Rapport Annuel & Évolution → identiques mais avec GNF et vraies données */}
          {reportType === 'yearly' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 mr-4">
                      <i className="ri-file-list-3-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Bulletins Émis</p>
                      <p className="text-2xl font-bold text-gray-900">{yearlyStats.totalPayrolls}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100 mr-4">
                      <i className="ri-money-dollar-circle-line text-xl text-green-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Salaires Nets Annuels</p>
                      <p className="text-2xl font-bold text-gray-900">{formatGNF(yearlyStats.totalNetSalary)}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-orange-100 mr-4">
                      <i className="ri-calculator-line text-xl text-orange-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Charges Sociales Totales</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatGNF(yearlyStats.totalEmployeeCharges + yearlyStats.totalEmployerCharges)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100 mr-4">
                      <i className="ri-building-line text-xl text-purple-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Coût Total Annuel</p>
                      <p className="text-2xl font-bold text-gray-900">{formatGNF(yearlyStats.totalEmployerCost)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Répartition par Département</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Object.entries(departmentStats).map(([dept, stats]) => (
                      <div key={dept} className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">{dept}</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Employés:</span>
                            <span className="text-sm font-medium text-gray-900">{stats.employees}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Masse salariale mensuelle:</span>
                            <span className="text-sm font-medium text-gray-900">{formatGNF(stats.totalSalary)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Salaire moyen:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {formatGNF(stats.totalSalary / stats.employees)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {reportType === 'evolution' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Évolution Mensuelle {selectedYear}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mois</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employés</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salaire Brut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salaire Net</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Charges Sociales</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {monthlyEvolution.map((month) => (
                      <tr key={month.month} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{month.month}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.employees}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatGNF(month.grossSalary)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatGNF(month.netSalary)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">{formatGNF(month.charges)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>

      {/* Boutons d'export dynamiques */}
      <div className="flex justify-end gap-4 pt-4">
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          <i className="ri-file-excel-2-line text-lg"></i>
          Exporter Excel
        </button>

        <button
          onClick={exportToPDF}
          className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
        >
          <i className="ri-file-pdf-line text-lg"></i>
          Exporter PDF
        </button>
      </div>
    </div>
  );
}